import React from 'react';
import { motion } from 'framer-motion';
import { highlightTerms } from '../highlightTerms';
import { ArrowRight } from 'lucide-react';

export const PhysicsSlide = ({ slide }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: 'easeOut', delay: 0.3 } },
  };

  return (
    <div
      className="h-full w-full grid grid-cols-1 lg:grid-cols-5 gap-8 items-center px-8 md:px-12 lg:px-16 py-12"
      data-testid={`slide-${slide.id}`}
    >
      {/* Left side - Text content (3 columns) */}
      <motion.div
        className="lg:col-span-3 space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2
          variants={itemVariants}
          className="pres-font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-none"
        >
          {highlightTerms(slide.title)}
        </motion.h2>

        {/* Formulas */}
        {slide.formulas && (
          <div className="flex flex-wrap gap-4 mt-8">
            {slide.formulas.map((item, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="bg-[#2A1D45]/60 backdrop-blur-md border border-[#D31113]/30 rounded-xl p-6 hover:border-[#D31113] transition-colors overflow-hidden"
              >
                <div className="formula text-2xl md:text-3xl font-bold text-white mb-2 break-words">
                  {item.formula}
                </div>
                <p className="font-sans text-sm md:text-base text-gray-400">
                  {highlightTerms(item.label)}
                </p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Bullets */}
        {slide.bullets && (
          <motion.div className="space-y-3 mt-6">
            {slide.bullets.map((bullet, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="flex items-center gap-3"
              >
                <ArrowRight className="w-5 h-5 text-[#D31113] flex-shrink-0" />
                <p className="font-sans text-lg md:text-xl text-gray-200">
                  {highlightTerms(bullet)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Key Line */}
        {slide.keyLine && (
          <motion.div
            variants={itemVariants}
            className="mt-6 py-4 px-6 bg-[#D31113]/10 border border-[#D31113]/40 rounded-xl inline-block"
          >
            <p className="font-sans text-lg md:text-xl text-white font-bold">
              {highlightTerms(slide.keyLine)}
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Right side - Newton Image (2 columns) */}
      <motion.div
        className="lg:col-span-2 flex items-center justify-center"
        variants={imageVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="relative">
          {/* Decorative frame */}
          <div className="absolute -inset-4 bg-gradient-to-br from-[#D31113]/20 to-transparent rounded-2xl" />
          <div className="relative overflow-hidden rounded-xl border-2 border-[#D31113]/30 shadow-2xl shadow-[#D31113]/20">
            <img
              src={slide.image}
              alt="Sir Isaac Newton"
              className="w-full max-w-sm h-auto object-cover"
              style={{ maxHeight: '450px' }}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#17072F] to-transparent p-4">
              <p className="text-white/80 text-sm font-sans text-center">Sir Isaac Newton</p>
              <p className="text-white/50 text-xs font-sans text-center">1643 - 1727</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
